'use client';

import { useTranslations } from 'next-intl';
import type { CourtFeed } from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import { embedHostAllowed, safeWatchUrl } from '@/app/[locale]/event/[eventId]/libs/streamLinks';
import { StreamLinkCard } from './StreamLinkCard';
import { platformName } from './streamPlatforms';

/** The outbound links of an inline feed: the feed itself, then each other destination. */
export function FeedLinks({
  feed,
  includePrimary = true,
}: {
  feed: CourtFeed;
  includePrimary?: boolean;
}) {
  const t = useTranslations('EventWatch');
  return (
    <>
      {includePrimary && (
        <a
          href={feed.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary self-start text-sm underline"
        >
          {t('openOn', { platform: platformName(feed.platform, feed.url) })}
        </a>
      )}
      {feed.alsoOn.map((other) => {
        const href = safeWatchUrl(other.url);
        if (!href) return null;
        return (
          <a
            key={other.url}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary self-start text-sm underline"
          >
            {t('openOn', { platform: platformName(other.platform, other.url) })}
          </a>
        );
      })}
    </>
  );
}

const YOUTUBE_EMBED_HOSTS = new Set(['www.youtube.com', 'www.youtube-nocookie.com']);

/** A YouTube video embed (`/embed/<id>`, never `live_stream`) started at `startSec`; anything else unchanged. */
function withStart(embedUrl: string, startSec?: number): string {
  if (!startSec || startSec <= 0) return embedUrl;
  try {
    const u = new URL(embedUrl);
    if (
      !YOUTUBE_EMBED_HOSTS.has(u.hostname) ||
      !/^\/embed\/(?!live_stream$)[^/]+$/.test(u.pathname)
    )
      return embedUrl;
    u.searchParams.set('start', String(Math.floor(startSec)));
    return u.toString();
  } catch {
    return embedUrl;
  }
}

/**
 * One feed. Only an allow-listed embed host is ever put in an iframe (never the stored URL), and
 * an inline player carries the outbound links unless the caller shows them itself
 * (`links="none"`). Everything else is a link card (`compact` for the Watch tab's short card).
 */
export function StreamPlayer({
  feed,
  title,
  compact = false,
  links = 'below',
  startSec,
}: {
  feed: CourtFeed;
  title: string;
  compact?: boolean;
  links?: 'below' | 'none';
  /** Start a YouTube video embed this many seconds in. */
  startSec?: number;
}) {
  const embedUrl = feed.display === 'inline' ? feed.embedUrl : null;
  if (!embedUrl || !embedHostAllowed(embedUrl))
    return <StreamLinkCard feed={feed} compact={compact} title={title} />;
  const src = withStart(embedUrl, startSec);
  if (!embedHostAllowed(src)) return <StreamLinkCard feed={feed} compact={compact} title={title} />;
  return (
    <div className="flex flex-col gap-2">
      <div className="bg-foreground aspect-video w-full overflow-hidden rounded-lg">
        <iframe
          src={src}
          title={title}
          className="h-full w-full border-0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      {links === 'below' && <FeedLinks feed={feed} />}
    </div>
  );
}
